import { useContext, useEffect, useMemo, useState } from "react";
import { Button, Select } from "../ui";
import { Context } from "../../context/Context";
import { useAccess } from "../../access/hooks/useAccess";
import { formatSar } from "../../utils/billingMoney";
import {
  downloadInvoicePdfAPI,
  getInvoiceAPI,
  getPaymentReceiptUrlAPI,
  getRestaurantBillingAPI,
  listAccessibleBillingRestaurantsAPI,
  listBillingStoresAPI,
  listInvoicesAPI,
  listServiceCatalogAPI,
  addSubscriptionBranchAPI,
} from "../../utils/api/billing";
import ReceiptUploadModal from "./ReceiptUploadModal";
import RestaurantInvoiceDetailsModal from "./RestaurantInvoiceDetailsModal";
import SubscribeModal from "./SubscribeModal";
import ManageSubscriptionModal from "./ManageSubscriptionModal";
import StopSubscriptionModal from "./StopSubscriptionModal";
import ServiceOrderModal from "./ServiceOrderModal";
import AiWalletPanel from "./aiWallet/AiWalletPanel";
import { useBillingAdminText } from "./admin/billingAdminI18n";
import { BRANCH_STATUS_LABEL_KEY, fillTemplate, useClientBillingText } from "./clientBillingText";
import BranchActivationSection from "./BranchActivationSection";
import "./admin/BillingAdmin.css";
import "./RestaurantBilling.css";

const formatUtcDay = (value, lang = "ar") => (value ? new Date(value).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", { timeZone: "UTC" }) : "-"); // billing days are UTC days: an end date of 23:59 UTC must not read as the next day in local time
const formatDate = (value, lang = "ar") => {
  if (!value) return "-";
  return new Intl.DateTimeFormat(lang === "en" ? "en-US" : "ar-SA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone: "UTC",
  }).format(new Date(value));
};

// Account-level state derived purely from what's already on screen, mirroring
// the v141 5-state subscription machine (active / scheduled_stop /
// scheduled_start / pending / none) closely enough for badge purposes - the
// backend remains the source of truth for the underlying status field.
function subscriptionBadge(subscription, text) {
  if (!subscription) return { key: "none", label: text.noneBadge, variant: "badge-neutral" };
  if (subscription.status === "active") return { key: "active", label: text.activeBadge, variant: "badge-success" };
  if (subscription.status === "scheduled_for_cancellation")
    return { key: "scheduled_stop", label: text.scheduledStopBadge, variant: "badge-warning" };
  if (subscription.status === "draft") return { key: "pending", label: text.pendingBadge, variant: "badge-warning" };
  return { key: "other", label: subscription.status, variant: "badge-neutral" };
}

// `preview` is the ceo/cto "View as Store" mode: the same page a store sees,
// read-only. The session and permissions are unchanged and no action in this
// page can run (every modal that would write is blocked, capabilities are empty).
const RestaurantBilling = ({ access, preview = false, onExitPreview }) => {
  const token = localStorage.getItem("auth-token");
  const { lang } = useContext(Context);
  const isRtl = lang !== "en";
  const text = useClientBillingText(isRtl ? "ar" : "en");
  const adminText = useBillingAdminText(lang); // reused only by ReceiptUploadModal/InvoiceDetailsModal

  const [restaurants, setRestaurants] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const { can } = useAccess();
  const [modal, setModalState] = useState(null);
  const setModal = (next) => {
    // preview: only the read-only invoice details may open
    if (preview && next && next.type !== "invoice") return;
    // handler-level authorization: writes (subscribe, order, receipt, manage) are for the store's own page,
    // and only for a restaurant the ownership API returned (`/me/restaurants`). The restaurant is verified
    // against that list, not against `user.between`: the backend also grants ownership by the account's
    // email / national id (billingAccess.findOwnedStoreIds), so `between` can be incomplete (NA-011).
    if (!preview && next && next.type !== "invoice") {
      const currentId = storeId || restaurants[0]?.id || "";
      if (!can("billing.client.view") || !currentId || !restaurants.some((r) => r.id === currentId)) return;
    }
    setModalState(next);
  };
  const [downloading, setDownloading] = useState("");
  // Bumped whenever the page refreshes so the AI Wallet section reloads too.
  const [walletReloadKey, setWalletReloadKey] = useState(0);

  const selectedStoreId = storeId || restaurants[0]?.id || "";

  const loadRestaurants = async () => {
    const result = preview
      ? await listBillingStoresAPI(token)
      : await listAccessibleBillingRestaurantsAPI(token);
    const nextRestaurants = preview ? result.data || [] : result.data?.restaurants || [];
    setRestaurants(nextRestaurants);
    // Arriving from another module for one restaurant (?storeId=, e.g. the
    // Menu page's AI Wallet link): open that one when it is in the list.
    const requested = new URLSearchParams(window.location.search).get("storeId");
    const first = nextRestaurants.find((r) => requested && r.id === requested)?.id || nextRestaurants[0]?.id || "";
    if (!storeId && first) setStoreId(first);
    return first;
  };

  const loadOverview = async (nextStoreId = selectedStoreId) => {
    setOverviewLoading(true);
    setOverviewError("");
    try {
      const result = await getRestaurantBillingAPI(token, { storeId: nextStoreId });
      setOverview(result.data);
    } catch (err) {
      setOverviewError(err.message);
    } finally {
      setOverviewLoading(false);
    }
  };

  const loadInvoices = async (nextStoreId = selectedStoreId) => {
    if (!nextStoreId) return;
    setInvoicesLoading(true);
    try {
      const result = await listInvoicesAPI(token, { storeId: nextStoreId, limit: 50 });
      setInvoices(result.data || []);
    } catch (err) {
      setOverviewError(err.message);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const loadServiceCatalog = async () => {
    try {
      const result = await listServiceCatalogAPI(token);
      setServiceCatalog(result.data || []);
    } catch (err) {
      // non-fatal: catalog is additive to the page
    }
  };

  const refreshAll = async (nextStoreId = selectedStoreId) => {
    await Promise.all([loadOverview(nextStoreId), loadInvoices(nextStoreId)]);
  };

  useEffect(() => {
    const boot = async () => {
      const firstStoreId = await loadRestaurants();
      await Promise.all([refreshAll(firstStoreId), loadServiceCatalog()]);
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onStoreChange = async (event) => {
    const nextStoreId = event.target.value;
    setStoreId(nextStoreId);
    await refreshAll(nextStoreId);
  };

  const openInvoice = async (invoice) => {
    try {
      const result = await getInvoiceAPI(token, invoice._id);
      setModal({ type: "invoice", invoice: result.data });
    } catch (err) {
      setOverviewError(err.message);
    }
  };

  const openReceipt = async (payment) => {
    try {
      const result = await getPaymentReceiptUrlAPI(token, payment._id);
      window.open(result.data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setOverviewError(err.message);
    }
  };

  const downloadInvoice = async (invoice) => {
    setDownloading(`invoice:${invoice._id}`);
    try {
      await downloadInvoicePdfAPI(token, invoice._id, lang);
    } catch (err) {
      setOverviewError(err.message);
    } finally {
      setDownloading("");
    }
  };

  const [addingCoverage, setAddingCoverage] = useState("");
  const [coverageError, setCoverageError] = useState("");
  // A client adds a branch to THEIR subscription: the server prices it in full for the current cycle and issues the invoice.
  const addCoverage = async (subscriptionId, branchId) => {
    setAddingCoverage(branchId);
    setCoverageError("");
    try {
      const result = await addSubscriptionBranchAPI(token, subscriptionId, branchId, {});
      await refreshAll(selectedStoreId);
      if (result.data?.invoice) setModal({ type: "invoice", invoice: result.data.invoice });
      else setModal(null);
    } catch (err) {
      setCoverageError(err.message);
    } finally {
      setAddingCoverage("");
    }
  };

  const closeModalAndRefresh = async () => {
    setModal(null);
    setWalletReloadKey((key) => key + 1);
    await refreshAll(selectedStoreId);
  };

  const restaurant = overview?.restaurant;
  const capabilities = preview ? {} : overview?.capabilities || {};
  const platformSub = overview?.subscriptions?.platform || null;
  const integrationSub = overview?.subscriptions?.integration || null;
  const platformBranchTrack = overview?.branchTracks?.platform || [];
  const integrationBranchTrack = overview?.branchTracks?.integration || [];
  const eligibleIntegrationBranchIds = overview?.integrationEligibility?.eligibleBranchIds || [];
  const branches = restaurant?.branches || [];

  const platformBadge = subscriptionBadge(platformSub, text);
  const contextBadge = platformBadge; // context strip mirrors the LUD platform state (v141 §1)

  const groupedInvoices = useMemo(() => {
    const groups = { awaiting_payment: [], finance_review: [], paid: [], cancelled: [] };
    invoices.forEach((invoice) => {
      const bucket = invoice.bucket || "awaiting_payment";
      (groups[bucket] || groups.awaiting_payment).push(invoice);
    });
    return groups;
  }, [invoices]);

  const needsActionCount = groupedInvoices.awaiting_payment.length;

  const platformPlans = (overview?.availablePlans || []).filter((plan) => plan.type === "platform");
  const integrationPlan = (overview?.availablePlans || []).find((plan) => plan.type === "integration");

  const hasPendingPlatformRequest = platformSub?.status === "draft";
  const hasPendingIntegrationRequest = integrationSub?.status === "draft";

  const payable = (invoice) =>
    invoice.status !== "void" && invoice.paymentStatus !== "paid" && Number(invoice.balanceDueMinor || 0) > 0;

  return (
    <main className="client-billing" dir={isRtl ? "rtl" : "ltr"}>
      {preview && (
        <section className="cb-preview-banner" role="status">
          <span>{isRtl ? "معاينة كمتجر (للعرض فقط)" : "Viewing as a store (read-only preview)"}</span>
          {onExitPreview && (
            <Button appearance="outline" onClick={onExitPreview}>
              {isRtl ? "العودة إلى عرض الإدارة" : "Back to Admin View"}
            </Button>
          )}
        </section>
      )}
      <section className="cb-context-strip">
        <div className="cb-context-main">
          <span className="cb-eyebrow">{text.pageEyebrow}</span>
          <h1>
            {overviewLoading
              ? text.contextLoading
              : restaurant?.businessName || restaurant?.name || text.pageTitle}
          </h1>
          <div className="cb-context-meta">
            <span>
              {text.branchesLabel}: <strong>{restaurant?.branchCount ?? "-"}</strong>
            </span>
            <span>
              {text.currentPackageLabel}:{" "}
              <strong>{platformSub?.planSnapshot?.code || text.noneBadge}</strong>
            </span>
          </div>
        </div>
        <div className="cb-context-status">
          {restaurants.length > 1 && (
            <Select
              label={adminText.common.restaurant}
              value={selectedStoreId}
              onChange={onStoreChange}
              options={restaurants.map((r) => ({ value: r.id, label: r.businessName || r.name || r.id }))}
            />
          )}
          <span className={contextBadge.variant}>{contextBadge.label}</span>
          <span className="cb-context-note">{text.activationNote}</span>
        </div>
      </section>

      {overviewError && (
        <div className="cb-error">
          {overviewError}{" "}
          <Button size="sm" appearance="outline" onClick={() => refreshAll(selectedStoreId)}>
            {text.retry}
          </Button>
        </div>
      )}

      {/* ---- Branch activation and coverage (server-calculated) ---- */}
      <BranchActivationSection
        activation={overview?.branchActivation}
        mode={overview?.entitlementMode}
        lang={lang}
        canAct={Boolean(capabilities?.canSelfServiceSubscribe || capabilities?.canSelfServiceManageSubscription)}
        managedByLud={Boolean(platformSub && (platformSub.origin === "admin_grant" || platformSub.grant?.conversionPending))}
        onCover={() => {
          if (platformSub && platformSub.status === "active") setModal({ type: "manage", subType: "platform" });
          else if (platformPlans[0] && !hasPendingPlatformRequest) setModal({ type: "subscribe", mode: "subscribe", subType: "platform", plan: platformPlans[0] });
        }}
      />

      {/* ---- Subscriptions (#billingPanelSubscriptions) ---- */}
      <section className="cb-section" id="billingPanelSubscriptions">
        <h2 className="cb-section-title">{text.subscriptionsTitle}</h2>
        <div className="v83-subscription-columns">
          <SubscriptionCard
            eyebrow={text.ludEyebrow}
            title={
              platformSub
                ? fillTemplate(text.ludTitleTemplate, { package: platformSub.planSnapshot?.code })
                : text.ludTitleNone
            }
            badge={subscriptionBadge(platformSub, text)}
            subscription={platformSub}
            branchTrack={platformBranchTrack}
            branches={branches}
            plans={platformPlans}
            type="platform"
            capabilities={capabilities}
            hasPendingRequest={hasPendingPlatformRequest}
            lang={lang}
            text={text}
            onSubscribe={(plan) => setModal({ type: "subscribe", mode: "subscribe", subType: "platform", plan })}
            onUpgrade={(plan) =>
              setModal({
                type: "subscribe",
                mode: "upgrade",
                subType: "platform",
                plan,
                subscriptionId: platformSub._id,
              })
            }
            onManage={() => setModal({ type: "manage", subType: "platform" })}
          />
          <SubscriptionCard
            eyebrow={text.intgEyebrow}
            title={integrationSub ? text.intgTitle : text.intgTitleNone}
            badge={subscriptionBadge(integrationSub, text)}
            subscription={integrationSub}
            branchTrack={integrationBranchTrack}
            branches={branches}
            plans={integrationPlan ? [integrationPlan] : []}
            type="integration"
            capabilities={capabilities}
            hasPendingRequest={hasPendingIntegrationRequest}
            eligibleBranchIds={eligibleIntegrationBranchIds}
            lang={lang}
            text={text}
            onSubscribe={(plan) => setModal({ type: "subscribe", mode: "subscribe", subType: "integration", plan })}
            onManage={() => setModal({ type: "manage", subType: "integration" })}
            note={text.intgGateNote}
          />
        </div>
      </section>

      {/* ---- AI Wallet (#billingPanelAiWallet): prepaid AI credit, separate
           from the subscription above. A top-up is an invoice paid through
           the same receipt upload as any other invoice. ---- */}
      <AiWalletPanel
        storeId={selectedStoreId}
        lang={isRtl ? "ar" : "en"}
        reloadKey={walletReloadKey}
        readOnly={preview}
        onUploadReceipt={(invoice) => setModal({ type: "upload", invoice })}
        onChanged={() => loadInvoices(selectedStoreId)}
      />

      {/* ---- Packages & additional services (#billingPanelCatalog) ---- */}
      <section className="cb-section" id="billingPanelCatalog">
        <h2 className="cb-section-title">{text.catalogTitle}</h2>
        <h3>{text.packageCatalogTitle}</h3>
        <div className="cb-catalog-grid" id="packageCatalogGrid">
          {(overview?.availablePlans || []).map((plan) => (
            <PackageCard
              key={plan._id}
              plan={plan}
              lang={lang}
              text={text}
              disabled={
                plan.type === "platform" ? hasPendingPlatformRequest || !!platformSub : hasPendingIntegrationRequest || !!integrationSub
              }
              canSubscribe={capabilities.canSelfServiceSubscribe}
              onSubscribe={() =>
                setModal({ type: "subscribe", mode: "subscribe", subType: plan.type, plan })
              }
            />
          ))}
        </div>
        <h3>{text.serviceCatalogTitle}</h3>
        <div className="cb-catalog-grid" id="serviceCatalogGrid">
          {serviceCatalog.map((service) => (
            <ServiceCard
              key={service._id}
              service={service}
              lang={lang}
              text={text}
              canOrder={capabilities.canOrderService}
              onRequest={() => setModal({ type: "serviceOrder", service })}
            />
          ))}
        </div>
      </section>

      {/* ---- Invoices & payments (#billingPanelInvoices) ---- */}
      <section className="cb-section" id="billingPanelInvoices" data-section-alias="invoices">
        <h2 className="cb-section-title">
          {text.invoicesTitle}
          {needsActionCount > 0 && (
            <span className="badge-warning" style={{ marginInlineStart: 10 }}>
              {text.needsActionPill}: {needsActionCount}
            </span>
          )}
        </h2>
        <div className="cb-invoices-banner">{text.invoicesBanner}</div>
        {overview?.paymentInstructions && (
          <div className="cb-payment-instructions">
            <h3>{text.paymentInstructionsTitle}</h3>
            <dl>
              <div>
                <dt>{text.paymentBank}</dt>
                <dd dir="auto">{overview.paymentInstructions.bankName?.[isRtl ? "ar" : "en"]}</dd>
              </div>
              <div>
                <dt>{text.paymentAccountName}</dt>
                <dd dir="auto">{overview.paymentInstructions.accountName?.[isRtl ? "ar" : "en"]}</dd>
              </div>
              <div>
                <dt>{text.paymentAccountNumber}</dt>
                <dd dir="ltr">{overview.paymentInstructions.accountNumber}</dd>
              </div>
              <div>
                <dt>{text.paymentIban}</dt>
                <dd dir="ltr">{overview.paymentInstructions.iban}</dd>
              </div>
            </dl>
          </div>
        )}
        {invoicesLoading && <div className="cb-loading">{text.loading}</div>}

        <InvoiceGroup
          title={text.groupAwaitingPayment}
          badgeClass="badge-warning"
          invoices={groupedInvoices.awaiting_payment}
          open
          lang={lang}
          text={text}
          onView={openInvoice}
          onDownload={downloadInvoice}
          downloading={downloading}
        />
        <InvoiceGroup
          title={text.groupFinanceReview}
          badgeClass="badge-warning"
          invoices={groupedInvoices.finance_review}
          open
          lang={lang}
          text={text}
          onView={openInvoice}
          onDownload={downloadInvoice}
          downloading={downloading}
        />
        <InvoiceGroup
          title={text.groupPaid}
          badgeClass="badge-success"
          invoices={groupedInvoices.paid}
          lang={lang}
          text={text}
          onView={openInvoice}
          onDownload={downloadInvoice}
          downloading={downloading}
        />
        <InvoiceGroup
          title={text.groupCancelled}
          badgeClass="badge-danger"
          invoices={groupedInvoices.cancelled}
          lang={lang}
          text={text}
          onView={openInvoice}
          onDownload={downloadInvoice}
          downloading={downloading}
        />
      </section>

      {modal?.type === "subscribe" && (
        <SubscribeModal
          mode={modal.mode}
          type={modal.subType}
          plan={modal.plan}
          currentSubscriptionId={modal.subscriptionId}
          branches={branches}
          eligibleBranchIds={eligibleIntegrationBranchIds}
          lang={lang}
          text={text}
          onCancel={() => setModal(null)}
          onDone={closeModalAndRefresh}
        />
      )}
      {modal?.type === "manage" && (
        <ManageSubscriptionModal
          subscription={modal.subType === "platform" ? platformSub : integrationSub}
          branchTrack={modal.subType === "platform" ? platformBranchTrack : integrationBranchTrack}
          lang={lang}
          text={text}
          canManage={capabilities.canSelfServiceManageSubscription}
          branches={branches}
          adding={addingCoverage}
          error={coverageError}
          onAddCoverage={(branchId) => addCoverage((modal.subType === "platform" ? platformSub : integrationSub)._id, branchId)}
          onClose={() => setModal(null)}
          onRequestStop={() =>
            setModal({
              type: "stop",
              subscriptionId: (modal.subType === "platform" ? platformSub : integrationSub)._id,
            })
          }
        />
      )}
      {modal?.type === "stop" && (
        <StopSubscriptionModal
          subscriptionId={modal.subscriptionId}
          lang={lang}
          text={text}
          onCancel={() => setModal(null)}
          onDone={closeModalAndRefresh}
        />
      )}
      {modal?.type === "serviceOrder" && (
        <ServiceOrderModal
          service={modal.service}
          branches={branches}
          lang={lang}
          text={text}
          onCancel={() => setModal(null)}
          onDone={closeModalAndRefresh}
        />
      )}
      {modal?.type === "invoice" && (
        <RestaurantInvoiceDetailsModal
          invoice={modal.invoice}
          lang={lang}
          text={adminText}
          onClose={() => setModal(null)}
          onUpload={payable(modal.invoice) && !preview ? () => setModal({ type: "upload", invoice: modal.invoice }) : null}
          onOpenReceipt={openReceipt}
        />
      )}
      {modal?.type === "upload" && (
        <ReceiptUploadModal
          invoice={modal.invoice}
          lang={lang}
          text={adminText}
          defaultAmountMinor={modal.invoice.balanceDueMinor}
          onCancel={() => setModal(null)}
          onUploaded={closeModalAndRefresh}
        />
      )}
    </main>
  );
};

const SubscriptionCard = ({
  eyebrow,
  title,
  badge,
  subscription,
  branchTrack,
  branches,
  plans,
  type,
  capabilities,
  hasPendingRequest,
  eligibleBranchIds,
  lang,
  text,
  onSubscribe,
  onUpgrade,
  onManage,
  note,
}) => {
  const plan = plans?.[0];
  const canAct = capabilities?.canSelfServiceSubscribe;
  const showSubscribe = !subscription && plan && !hasPendingRequest;
  // a granted / converting subscription is managed by LUD: no self-service plan change, and its end date is not a "paid through" date
  const isLudManaged = Boolean(subscription && (subscription.origin === "admin_grant" || subscription.grant?.conversionPending));
  const showUpgrade =
    type === "platform" && subscription?.status === "active" && capabilities?.canSelfServiceManageSubscription && !isLudManaged;
  // Coverage is per branch: the subscription belongs to the store, each branch is covered (paid), awaiting payment, or not covered at all.
  const coveredCount = (branchTrack || []).filter((row) => row.status === "active").length;
  const awaitingCount = (branchTrack || []).filter((row) => row.status === "scheduled").length;
  const trackedIds = new Set((branchTrack || []).filter((row) => ["active", "scheduled"].includes(row.status)).map((row) => String(row.branchId)));
  const uncoveredCount = (branches || []).filter((branch) => !trackedIds.has(String(branch.branchId || branch.id || branch._id))).length;

  return (
    <div className="cb-sub-card">
      <div className="cb-sub-card-head">
        <div>
          <span className="cb-sub-eyebrow">{eyebrow}</span>
          <h3 className="cb-sub-title">{title}</h3>
        </div>
        <span className={badge.variant}>{badge.label}</span>
      </div>
      {subscription && (
        <div className="cb-sub-meta" data-testid={`cb-coverage-summary-${type}`}>
          <div>
            {text.coveredBranchesLabel}: <strong>{coveredCount}</strong> {text.ofLabel} <strong>{branches?.length ?? 0}</strong>
          </div>
          {awaitingCount > 0 && (
            <div>
              {text.awaitingPaymentBranchesLabel}: <strong>{awaitingCount}</strong>
            </div>
          )}
          {uncoveredCount > 0 && (
            <div>
              {text.uncoveredBranchesLabel}: <strong>{uncoveredCount}</strong>
            </div>
          )}
          {subscription.currentPeriodEnd && subscription.origin !== "admin_grant" && (
            <div>
              {text.paidThroughLabel}: {formatDate(subscription.currentPeriodEnd, lang)}
            </div>
          )}
          {subscription.origin === "admin_grant" && subscription.grant?.endDate && (
            <div data-testid={`cb-grant-note-${type}`}>
              {text.grantedByLudLabel}: {formatUtcDay(subscription.grant.endDate, lang)}
            </div>
          )}
          {subscription.grant?.conversionPending && subscription.grant?.endDate && (
            <div data-testid={`cb-conversion-note-${type}`}>
              {text.freeCoverageEndsLabel}: {formatUtcDay(subscription.grant.endDate, lang)}
            </div>
          )}
          {subscription.nextBillingDate && ["active", "past_due"].includes(subscription.status) && (
            <div>
              {text.renewalDateLabel}: {formatDate(subscription.nextBillingDate, lang)}
            </div>
          )}
        </div>
      )}
      {note && <div className="cb-sub-note">{note}</div>}
      {hasPendingRequest && <div className="cb-sub-note">{text.requestPending}</div>}

      <div className="cb-sub-actions">
        {showSubscribe && canAct && (
          <Button appearance="filled" onClick={() => onSubscribe(plan)}>
            {type === "integration" ? text.subscribeCta.replace("{package}", "INTG") : fillTemplate(text.subscribeCta, { package: plan.code })}
          </Button>
        )}
        {showUpgrade && plan && (
          <Button appearance="outline" onClick={() => onUpgrade(plan)}>
            {text.upgradeCta}
          </Button>
        )}
        {subscription && !(subscription.origin === "admin_grant" || subscription.grant?.conversionPending) && (
          <Button appearance="outline" onClick={onManage}>
            {text.manageCta}
          </Button>
        )}
      </div>

      <strong>{text.branchTrackTitle}</strong>
      {!branchTrack?.length ? (
        <div className="cb-branch-track-empty">{text.branchTrackEmpty}</div>
      ) : (
        <div className="cb-table-scroll">
          <table className="cb-branch-track">
            <thead>
              <tr>
                <th>{text.colBranch}</th>
                <th>{text.colStart}</th>
                <th>{text.colEnd}</th>
                <th>{text.colRemaining}</th>
                <th>{text.colStatus}</th>
              </tr>
            </thead>
            <tbody>
              {branchTrack.map((row) => (
                <tr key={row.branchId}>
                  <td>{row.branchName}</td>
                  <td>{formatDate(row.startDate, lang)}</td>
                  <td>{row.endDate ? formatDate(row.endDate, lang) : "-"}</td>
                  <td>
                    {row.remainingDays != null
                      ? fillTemplate(text.remainingDaysTemplate, { days: row.remainingDays })
                      : text.remainingOpenEnded}
                  </td>
                  <td>{text[BRANCH_STATUS_LABEL_KEY[row.status]] || row.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const PackageCard = ({ plan, lang, text, disabled, canSubscribe, onSubscribe }) => {
  const isIntg = plan.type === "integration";
  return (
    <div className={`cb-package-card${plan.code === "D" ? " featured" : ""}`}>
      <span className="cb-package-code">{plan.code}</span>
      <h3>{lang === "en" ? plan.nameEn : plan.nameAr}</h3>
      <div className="cb-price-line">{formatSar(plan.basePriceMinor, lang)}</div>
      <div className="cb-price-sub">{isIntg ? text.perBranchPerMonth : text.perMonth}</div>
      {!isIntg && (
        <div className="cb-price-sub">
          {text.extraBranchLabel}: {formatSar(plan.extraBranchPriceMinor, lang)}
        </div>
      )}
      {!!(plan.features || []).length && (
        <ul className="cb-feature-list">
          {plan.features.slice(0, 6).map((feature) => (
            <li key={feature}>{feature}</li>
          ))}
        </ul>
      )}
      <div className="cb-card-footer">
        <Button
          appearance="filled"
          fullWidth
          disabled={!canSubscribe || disabled}
          onClick={onSubscribe}
        >
          {isIntg ? text.subscribeCta.replace("{package}", "INTG") : fillTemplate(text.subscribeCta, { package: plan.code })}
        </Button>
      </div>
    </div>
  );
};

const ServiceCard = ({ service, lang, text, canOrder, onRequest }) => {
  const priceSuffix =
    service.billingMode === "per_unit"
      ? text.perPhoto
      : service.billingMode === "monthly"
        ? text.perMonth
        : service.billingMode === "per_branch"
          ? text.perBranchOnce
          : text.fixedPrice;
  return (
    <div className="cb-service-card">
      <h3>{lang === "en" ? service.nameEn : service.nameAr}</h3>
      <p className="cb-price-sub">{lang === "en" ? service.descriptionEn : service.descriptionAr}</p>
      <div className="cb-price-line">{formatSar(service.unitPriceMinor, lang)}</div>
      <div className="cb-price-sub">{priceSuffix}</div>
      {service.maxItems && (
        <div className="cb-price-sub">{fillTemplate(text.upToItems, { count: service.maxItems })}</div>
      )}
      <div className="cb-card-footer">
        <Button appearance="filled" fullWidth disabled={!canOrder} onClick={onRequest}>
          {text.requestCta}
        </Button>
      </div>
    </div>
  );
};

const invoiceLabel = (invoice) => invoice.pricingSnapshot?.planCode || invoice.items?.[0]?.descriptionAr || invoice.items?.[0]?.descriptionEn || "-";

const InvoiceGroup = ({ title, badgeClass, invoices, open, lang, text, onView, onDownload, downloading }) => (
  <details className="inv-group" open={open}>
    <summary>
      <span className={badgeClass}>{title}</span>
      <span className="cb-group-count">({invoices.length})</span>
    </summary>
    {!invoices.length ? (
      <div className="cb-invoice-empty">{text.invoicesEmpty}</div>
    ) : (
      <div className="cb-table-scroll">
        <table className="cb-invoice-table">
          <thead>
            <tr>
              <th>{text.colInvoiceNumber}</th>
              <th>{text.colInvoiceLabel}</th>
              <th>{text.colIssueDate}</th>
              <th>{text.colAmount}</th>
              <th>{text.colDueDate}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice) => (
              <tr key={invoice._id}>
                <td>{invoice.invoiceNumber || "-"}</td>
                <td>{invoiceLabel(invoice)}</td>
                <td>{formatDate(invoice.issueDate, lang)}</td>
                <td>{formatSar(invoice.totalMinor, lang)}</td>
                <td>{formatDate(invoice.dueDate, lang)}</td>
                <td>
                  <div className="billing-plan-actions">
                    <Button size="sm" appearance="outline" onClick={() => onView(invoice)}>
                      {text.view}
                    </Button>
                    {invoice.invoiceNumber && (
                      <Button
                        size="sm"
                        appearance="outline"
                        disabled={downloading === `invoice:${invoice._id}`}
                        onClick={() => onDownload(invoice)}
                      >
                        PDF
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </details>
);

export default RestaurantBilling;
